import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTable, useSortBy, useFilters } from 'react-table';
import fileDownload from 'js-file-download';
import {
  FaUser,
  FaCheckCircle,
  FaTimesCircle,
  FaFileDownload,
  FaExclamationTriangle,
} from 'react-icons/fa';

import { MdDeleteForever } from 'react-icons/md';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import SearchFilter from '~/components/Table/Filters/SearchFilter';
import SelectColumnFilterForStatus from './components/SelectFilterForStatus';
import { StatusBadge, ActionsContainer, Action } from './styles';
import history from '~/services/history';
import api from '~/services/api';

export default function Table({ data, handleDelete }) {
  const isAdmin = useSelector(state => state.user.profile.isAdmin);

  const columns = useMemo(() => {
    const defaultColumns = [
      {
        Header: 'ID',
        accessor: 'id',
        disableFilters: true,
        disableSortBy: true,
      },
      {
        Header: 'Name',
        accessor: item => {
          return `${item.firstName} ${item.lastName}`;
        },
        disableSortBy: true,
      },
      {
        Header: 'Phone',
        accessor: 'phoneNumber',
        disableSortBy: true,
        disableFilters: true,
      },
      {
        Header: 'Email',
        accessor: 'email',
        disableSortBy: true,
      },
      {
        Header: 'Vehicle',
        accessor: 'vehicle.displayName',
        disableSortBy: true,
      },
      {
        Header: 'Created at',
        accessor: 'created_at',
        disableFilters: true,
      },
      {
        Header: 'Status',
        accessor: item => {
          let status;
          if (item.status === null) {
            status = 'pending';
          } else if (item.status === true) {
            status = 'approved';
          } else if (item.status === false) {
            status = 'rejected';
          }
          return <StatusBadge value={status}>{status}</StatusBadge>;
        },
        Filter: SelectColumnFilterForStatus,
        filter: 'status',
        disableSortBy: true,
      },
      {
        Header: 'Actions',
        accessor: item => (
          <ActionsContainer>
            {item.status === null && (
              <>
                <Action
                  onClick={() =>
                    history.push({
                      pathname: '/client/create',
                      state: { item },
                    })
                  }
                >
                  <FaCheckCircle />
                  <span>Approve</span>
                </Action>
                <Action disabled onClick={() => {}}>
                  <FaTimesCircle />
                  <span>Reject</span>
                </Action>
              </>
            )}
            {item.status === true && (
              <Action
                onClick={() =>
                  history.push({
                    pathname: `/client/${item.client_id}`,
                  })
                }
              >
                <FaUser />
                <span>Client</span>
              </Action>
            )}
            <Action
              onClick={async () => {
                try {
                  const response = await api.get(
                    `financial/${item.id}/download`,
                    {
                      responseType: 'blob',
                    }
                  );
                  fileDownload(
                    response.data,
                    `${item.id}-${item.firstName}-${item.lastName}.pdf`
                  );
                  toast.success('Downloading...');
                } catch (err) {
                  toast.error(
                    err.response &&
                      err.response.data &&
                      err.response.data.error &&
                      typeof err.response.data.error === 'string'
                      ? err.response.data.error
                      : 'Error while generating pdf'
                  );
                }
              }}
            >
              <FaFileDownload />
              <span>Download</span>
            </Action>
          </ActionsContainer>
        ),
        disableFilters: true,
        disableSortBy: true,
      },
    ];
    const adminColumn = isAdmin && {
      id: 'admin',
      Header: () => <FaExclamationTriangle color="#CA0B00" />,
      accessor: item => {
        return (
          <ActionsContainer>
            <Action onClick={() => handleDelete(item.id)}>
              <MdDeleteForever color="#CA0B00" size={22} />
              <span>Delete</span>
            </Action>
          </ActionsContainer>
        );
      },
      disableFilters: true,
      disableSortBy: true,
    };
    if (isAdmin) {
      return [...defaultColumns, adminColumn];
    }
    return defaultColumns;
  }, [handleDelete, isAdmin]);
  const defaultColumn = React.useMemo(() => ({ Filter: SearchFilter }), []);
  const filterTypes = React.useMemo(
    () => ({
      status: (rows, id, filterValue) => {
        return rows.filter(row => {
          const rowValue = row.values[id];
          return rowValue !== undefined &&
            row.values[id].props !== undefined &&
            row.values[id].props.children !== undefined
            ? String(row.values[id].props.children)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true;
        });
      },
    }),
    []
  );
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      defaultColumn,
      filterTypes,
      data,
    },
    useFilters,
    useSortBy
  );
  return (
    <>
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render('Header')}
                  {column.isSorted ? (
                    <span>{column.isSortedDesc ? ' ↓' : ' ↑'}</span>
                  ) : (
                    ' '
                  )}
                  {column.canFilter && <div> {column.render('Filter')} </div>}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map(row => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map(cell => {
                  return (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

Table.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  handleDelete: PropTypes.func.isRequired,
};
